// Imported with the extension, which nothing else in src/ does, and for a
// reason rather than a whim: `scripts/gen-icons.mjs` runs this file in plain
// Node to read the palette, and Node's ESM resolver does not guess at one.
// `allowImportingTsExtensions` is already set, so the bundler is indifferent.
import { PALETTES } from './palettes.ts'
import type { PaletteName } from './palettes.ts'

/**
 * Design tokens. Deliberately free of React and Mantine so that the numbers
 * are importable from anywhere -- including the media-query hook, plain tests,
 * and eventually a non-web client -- without dragging a UI framework along.
 *
 * The breakpoint strings are duplicated in postcss.config.cjs, because PostCSS
 * cannot import TypeScript. Change both together.
 */

export const BREAKPOINTS = {
  xs: '36em',
  sm: '48em',
  md: '62em',
  lg: '75em',
  xl: '88em',
} as const

/**
 * The single boundary between the mobile and the desktop rendering. Everything
 * that switches layout switches here, so there is exactly one number to argue
 * about rather than one per component.
 */
export const DESKTOP_FROM = BREAKPOINTS.md

export const DESKTOP_MEDIA_QUERY = `(min-width: ${DESKTOP_FROM})`

/**
 * Default brand palette, also used for generated icons and the PWA manifest.
 * Runtime personal preferences live in lib/appearance and do not regenerate assets.
 */
export const PALETTE_NAME: PaletteName = 'dragon'

export const PALETTE = PALETTES[PALETTE_NAME]

/**
 * The widest the content column gets on desktop.
 *
 * Every page here is narrow content -- a name, a rank, a couple of controls,
 * a sheet of six ability scores -- and letting one span a 2560px monitor puts
 * the thing you are reading and the control that acts on it an eye movement
 * apart. Applied once, by `ui/Page`, so no screen has to remember it.
 *
 * It used to be `MAX_TABLE_WIDTH`, and it used to live inside `ui/DataList`,
 * where it capped tables and nothing else -- so the two screens that wanted
 * their heading to line up with the table underneath it copied the number by
 * hand, and the character sheet, which is not a table, was never capped at all.
 * It is a fact about the page now, which is why it is a layout token rather
 * than a component's private constant.
 */
export const CONTENT_MAX_WIDTH = 1024

/**
 * The height of one row of chrome, shared by the navbar's entries and the
 * page's heading line.
 *
 * Shared so that the two line up by construction. A section is named twice on
 * screen at once -- once in the navbar and again as the heading of the page it
 * opened -- and the two sat 4px apart, which is close enough to read as a
 * mistake rather than as a choice. Giving both the same row height, started at
 * the same offset below the header, puts them on one line at every zoom level
 * without either side nudging itself into place.
 *
 * 41 is what a navbar row measures with a word in it; see `shell/DesktopShell`,
 * which also pins the rail's rows to it so they do not shrink when the words go.
 */
export const ROW_HEIGHT = 41

/**
 * The inset the navbar and the content share at the top.
 *
 * The navbar's own padding, applied to the main area as well, so the first
 * navbar row and the page's heading row start at the same y.
 */
export const CHROME_INSET = 10

/**
 * The smallest a thing meant to be touched is drawn.
 *
 * The number every platform guideline agrees on, and it arrived here the way
 * good numbers do: `features/character/ScoreAssignment` worked it out for
 * itself, as a file-local `TARGET`, because the one gesture that surface exists
 * for was a few millimetres of badge.
 *
 * It is a floor for the things a thumb has to hit *precisely* -- a drag target,
 * a drop zone -- and deliberately not the size of every control. Making it so
 * was tried: at 390px this app is mostly controls, and inflating all of them
 * cost more in scrolling than it bought in accuracy.
 */
export const TOUCH_TARGET = 44

export const RADIUS_DEFAULT = 'md'
